import React from 'react';
import { Props } from '.';
import { compareEventsByTime, getPlayerNumber, getTeamId } from '../../../helpers/EventHelper';
import { getClientEventKey } from '../../../helpers/pendingEventHelpers';
import { shootoutRows } from '../../../helpers/shootoutOrder';
import {
    CreateEventCommand,
    CreateMatchPlayerCommandOfMatchPlayer,
    EventPeriodEnum,
    EventVm,
    UpdateEventCommand,
} from '../../../utils/api';
import MatchLogItem from './log-item';
import ShootoutOrderControls from '../ShootoutOrderControls';

class MatchEventLog extends React.PureComponent<Props> {
    private renderLogItem = (e: EventVm, shootoutControls?: React.ReactNode): React.ReactElement => {
        const { match, lineups, coaches, editEvent, onRefereeMistakeChange } = this.props;
        const tid = getTeamId(e);
        return (
            <MatchLogItem
                match={match!}
                event={e as unknown as UpdateEventCommand}
                isHomeTeam={tid === match!.homeTeamId}
                lineups={lineups}
                lineup={lineups.find(
                    (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                        l.number === getPlayerNumber(e) && l.teamId === tid
                )}
                coaches={coaches}
                editEvent={editEvent}
                onRefereeMistakeChange={onRefereeMistakeChange}
                shootoutOrder={e.shootoutOrder}
                shootoutControls={shootoutControls}
            />
        );
    };

    /** One row per event in game-time order; home (white) flush left, away (blue) flush right. */
    private renderTimelinePeriod = (periodEvents: EventVm[], controls?: (row: EventVm) => React.ReactNode): React.ReactElement | null => {
        const { match } = this.props;
        if (!match) {
            return null;
        }
        const homeId = match.homeTeamId;
        const awayId = match.awayTeamId;

        return (
            <>
                {periodEvents.map((e: EventVm): React.ReactElement => {
                    const tid = getTeamId(e);
                    const isOrphan = tid !== homeId && tid !== awayId;
                    const rowClass = isOrphan
                        ? 'o-event-list__align-row o-event-list__align-row--full'
                        : tid === homeId
                        ? 'o-event-list__align-row o-event-list__align-row--home'
                        : 'o-event-list__align-row o-event-list__align-row--away';
                    return (
                        <div key={getClientEventKey(e) || e.id} className={rowClass}>
                            {this.renderLogItem(e, controls?.(e))}
                        </div>
                    );
                })}
            </>
        );
    };

    public render = (): React.ReactElement => {
        const { events, match, shootoutEditing } = this.props;
        const sortedEvents = [...events].sort(compareEventsByTime);

        const q1Events = sortedEvents.filter(
            (e: EventVm | CreateEventCommand): boolean =>
                e.periodId === EventPeriodEnum.First_Period
        ) as EventVm[];
        const q2Events = sortedEvents.filter(
            (e: EventVm): boolean => e.periodId === EventPeriodEnum.Second_Period
        );
        const q3Events = sortedEvents.filter(
            (e: EventVm): boolean => e.periodId === EventPeriodEnum.Third_Period
        );
        const q4Events = sortedEvents.filter(
            (e: EventVm): boolean => e.periodId === EventPeriodEnum.Fourth_Period
        );
        const psoEvents = shootoutRows(events).reverse();
        const overtimeEvents = sortedEvents.filter(
            (e: EventVm): boolean => e.periodId === EventPeriodEnum.Overtime && e.shootoutOrder == null
        );

        return (
            <div className="o-event-list">
                {match && psoEvents.length > 0 && (shootoutEditing
                    ? <ShootoutOrderControls match={match} events={events} {...shootoutEditing}>
                        {controls => this.renderTimelinePeriod(psoEvents, controls)}
                    </ShootoutOrderControls>
                    : this.renderTimelinePeriod(psoEvents))}
                {match && psoEvents.length > 0 && (
                    <span className="event-log-quarter">Penalty shootout started</span>
                )}
                {match && this.renderTimelinePeriod(overtimeEvents)}
                {match && overtimeEvents.length > 0 && (
                    <span className="event-log-quarter">Overtime started</span>
                )}
                {match && this.renderTimelinePeriod(q4Events)}
                {match && q4Events.length > 0 && (
                    <span className="event-log-quarter">4th period started</span>
                )}
                {match && this.renderTimelinePeriod(q3Events)}
                {match && q3Events.length > 0 && (
                    <span className="event-log-quarter">3rd period started</span>
                )}
                {match && this.renderTimelinePeriod(q2Events)}
                {match && q2Events.length > 0 && (
                    <span className="event-log-quarter">2nd period started</span>
                )}
                {match && this.renderTimelinePeriod(q1Events)}
                {match && q1Events.length > 0 && (
                    <span className="event-log-quarter">1st period started</span>
                )}
            </div>
        );
    };
}

export default MatchEventLog;
